// Base operation interface
interface BaseOperation {
  id: string; // Unique operation ID
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  timestamp: number; // When operation was queued
  errorMessage?: string; // Last failure reason, if processing failed
}

// Consume operation - decrements stock
export interface ConsumeOperation extends BaseOperation {
  type: "CONSUME";
  productId: number; // Product to consume
  amount: number; // Amount to consume in one operation
  transactionId?: string; // Backend transaction ID (set when completed)
}

// Undo operation - reverts a previous consume
export interface UndoOperation extends BaseOperation {
  type: "UNDO";
  targetOperationId: string; // ID of the consume operation to undo
  productId: number; // Product ID (derived from target operation for UI updates)
}

// Discriminated union type
export type QueueOperation = ConsumeOperation | UndoOperation;

// Callbacks for the processor to communicate with the hook
export interface ProcessorCallbacks {
  updateOperationStatus: (
    operationId: string,
    status: QueueOperation["status"],
  ) => void;
  updateOperation: (
    operationId: string,
    updates: Partial<QueueOperation>,
  ) => void;
  onQueueEmpty: () => void;
  onOperationFailed: (operation: QueueOperation) => void;
  onOperationCompleted: (operation: QueueOperation) => void;
  removeCompletedPair: (consumeId: string, undoId: string) => void;
}

// Type guard functions
export function isConsumeOperation(op: QueueOperation): op is ConsumeOperation {
  return op.type === "CONSUME";
}

export function isUndoOperation(op: QueueOperation): op is UndoOperation {
  return op.type === "UNDO";
}

// Helper to check if operation can be cancelled (removed from queue)
export function canCancelOperation(op: QueueOperation): boolean {
  return op.status === "PENDING";
}

// Helper to find undoable operations
export function findUndoableOperations(
  queue: QueueOperation[],
): ConsumeOperation[] {
  return queue
    .filter(isConsumeOperation)
    .filter((op) => op.status !== "FAILED");
}

// Helper to check if consume operation already has pending undo
export function hasExistingUndo(
  consumeOperation: ConsumeOperation,
  queue: QueueOperation[],
): boolean {
  return queue.some((op) =>
    op.type === "UNDO" &&
    op.status !== "FAILED" &&
    op.targetOperationId === consumeOperation.id
  );
}

// Generate unique operation ID
export function generateUniqueId(): string {
  return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
